import type { ReactNode } from "react";
import type { PartnerDto } from "../../api";
import { Heading, Text } from "./typography";
import { IconPill } from "./icon-pill";

interface PartnerAddressCardProps {
  title: string;
  icon: ReactNode;
  partner: PartnerDto | null | undefined;
  /** Hide contact + tax fields and only show the formatted street address. */
  addressOnly?: boolean;
}

/**
 * Read-only card that renders a partner's address + contact info. The single
 * presentation primitive shared by the order, purchase-order, and any other
 * "who is this addressed to" surface so they all stay visually aligned with
 * the saved-addresses cards in account settings.
 */
export function PartnerAddressCard({
  title,
  icon,
  partner,
  addressOnly = false,
}: PartnerAddressCardProps) {
  if (!partner) return null;

  const cityLine = [partner.city, partner.state?.name, partner.zip]
    .filter(Boolean)
    .join(", ");

  const hasAddressLines =
    partner.street ||
    partner.street2 ||
    cityLine ||
    partner.country?.name;

  return (
    <div className="flex flex-1 min-w-[280px] flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <div className="flex items-center gap-2">
        <IconPill size={36} tone="ghost" shape="square">{icon}</IconPill>
        <Heading variant="h3" tone="dark">{title}</Heading>
      </div>

      <div className="flex flex-col gap-0.5">
        {partner.name && (
          <Text variant="bodyBold" tone="dark">{partner.name}</Text>
        )}

        {hasAddressLines ? (
          <div className="flex flex-col gap-0.5">
            {partner.street && (
              <Text variant="bodySm" tone="body">{partner.street}</Text>
            )}
            {partner.street2 && (
              <Text variant="bodySm" tone="body">{partner.street2}</Text>
            )}
            {cityLine && (
              <Text variant="bodySm" tone="body">{cityLine}</Text>
            )}
            {partner.country?.name && (
              <Text variant="bodySm" tone="body">{partner.country.name}</Text>
            )}
          </div>
        ) : (
          <Text variant="bodySm" tone="muted">No address on file</Text>
        )}

        {!addressOnly && (partner.phone || partner.email) && (
          <div className="mt-2 flex flex-col gap-0.5">
            {partner.phone && (
              <Text variant="bodySm" tone="body">Phone: {partner.phone}</Text>
            )}
            {partner.email && (
              <Text variant="bodySm" tone="body">Email: {partner.email}</Text>
            )}
          </div>
        )}

        {!addressOnly && (partner.vat || partner.companyRegistry) && (
          <div className="mt-2 flex flex-col gap-0.5">
            {partner.vat && (
              <Text variant="bodyXs" tone="muted">VAT: {partner.vat}</Text>
            )}
            {partner.companyRegistry && (
              <Text variant="bodyXs" tone="muted">CR: {partner.companyRegistry}</Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
